import {
  CreditScore,
  CreditDataSources,
  ScoreComponent,
  ScoreFactor,
  RiskTier,
  CreditDecision,
  DecisionRules,
  CreditScoreHistory,
  Recommendation,
} from '../types/creditScore'

// Credit Score Calculation Engine
export class CreditScoringEngine {
  // Component Weights (must sum to 100%)
  private static readonly WEIGHTS = {
    paymentHistory: 0.4,
    incomeStability: 0.25,
    debtExposure: 0.15,
    platformBehavior: 0.1,
    stabilityTenure: 0.1,
  }

  // Risk Tier Thresholds
  private static readonly RISK_TIERS: Record<
    RiskTier,
    { min: number; max: number }
  > = {
    A: { min: 750, max: 850 },
    B: { min: 650, max: 749 },
    C: { min: 550, max: 649 },
    D: { min: 0, max: 549 },
  }

  /**
   * Calculate comprehensive credit score
   */
  static calculateCreditScore(data: CreditDataSources): CreditScore {
    // Calculate each component
    const paymentHistory = this.calculatePaymentHistory(data)
    const incomeStability = this.calculateIncomeStability(data)
    const debtExposure = this.calculateDebtExposure(data)
    const platformBehavior = this.calculatePlatformBehavior(data)
    const stabilityTenure = this.calculateStabilityTenure(data)

    // Calculate weighted scores
    const components = {
      paymentHistory: {
        ...paymentHistory,
        weight: this.WEIGHTS.paymentHistory,
        weightedScore: paymentHistory.score * this.WEIGHTS.paymentHistory,
      },
      incomeStability: {
        ...incomeStability,
        weight: this.WEIGHTS.incomeStability,
        weightedScore: incomeStability.score * this.WEIGHTS.incomeStability,
      },
      debtExposure: {
        ...debtExposure,
        weight: this.WEIGHTS.debtExposure,
        weightedScore: debtExposure.score * this.WEIGHTS.debtExposure,
      },
      platformBehavior: {
        ...platformBehavior,
        weight: this.WEIGHTS.platformBehavior,
        weightedScore: platformBehavior.score * this.WEIGHTS.platformBehavior,
      },
      stabilityTenure: {
        ...stabilityTenure,
        weight: this.WEIGHTS.stabilityTenure,
        weightedScore: stabilityTenure.score * this.WEIGHTS.stabilityTenure,
      },
    }

    // Calculate final scores (0-100 scale, then convert to 0-850)
    const rawScore =
      components.paymentHistory.weightedScore +
      components.incomeStability.weightedScore +
      components.debtExposure.weightedScore +
      components.platformBehavior.weightedScore +
      components.stabilityTenure.weightedScore

    const generalScore = Math.round(rawScore * 8.5) // Convert to 0-850 scale
    const rentScore = this.calculateRentScore(data, generalScore)
    const riskTier = this.determineRiskTier(generalScore)
    const defaultProbability = this.calculateDefaultProbability(
      generalScore,
      data,
    )

    return {
      generalScore,
      rentScore,
      riskTier,
      defaultProbability,
      components,
      lastUpdated: new Date().toISOString(),
      nextUpdate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days
      dataCompleteness: this.calculateDataCompleteness(data),
    }
  }

  /**
   * Calculate Payment History Component (40% weight)
   */
  private static calculatePaymentHistory(
    data: CreditDataSources,
  ): Omit<ScoreComponent, 'weight' | 'weightedScore'> {
    const factors: ScoreFactor[] = []
    let score = 100

    // Platform rent payments
    if (data.platformData.rentPayments.length > 0) {
      const totalPayments = data.platformData.rentPayments.length
      const onTimePayments = data.platformData.rentPayments.filter(
        (p) => p.status === 'on-time',
      ).length
      const latePayments = data.platformData.rentPayments.filter(
        (p) => p.status === 'late',
      ).length
      const missedPayments = data.platformData.rentPayments.filter(
        (p) => p.status === 'missed',
      ).length

      const onTimeRate = (onTimePayments / totalPayments) * 100

      if (onTimeRate >= 95) {
        factors.push({
          name: 'Excellent Payment History',
          impact: 'positive',
          value: `${onTimeRate.toFixed(0)}%`,
          description: `${onTimePayments} of ${totalPayments} payments on time`,
        })
      } else if (onTimeRate >= 80) {
        score -= 10
        factors.push({
          name: 'Good Payment History',
          impact: 'neutral',
          value: `${onTimeRate.toFixed(0)}%`,
          description: `${latePayments} late payments`,
        })
      } else {
        score -= 25
        factors.push({
          name: 'Poor Payment History',
          impact: 'negative',
          value: `${onTimeRate.toFixed(0)}%`,
          description: `${missedPayments} missed, ${latePayments} late payments`,
        })
      }
    }

    // Utility payments
    if (data.alternativeData.utilityPayments.length > 0) {
      const totalUtilityPayments = data.alternativeData.utilityPayments.reduce(
        (sum, u) => sum + u.onTimePayments + u.latePayments + u.missedPayments,
        0,
      )
      const onTimeUtilityPayments = data.alternativeData.utilityPayments.reduce(
        (sum, u) => sum + u.onTimePayments,
        0,
      )
      const utilityRate = (onTimeUtilityPayments / totalUtilityPayments) * 100

      if (utilityRate >= 90) {
        factors.push({
          name: 'Strong Utility Payment Record',
          impact: 'positive',
          value: `${utilityRate.toFixed(0)}%`,
          description: 'Consistent utility payments',
        })
      } else {
        score -= 5
        factors.push({
          name: 'Inconsistent Utility Payments',
          impact: 'negative',
          value: `${utilityRate.toFixed(0)}%`,
          description: 'Some late utility payments',
        })
      }
    }

    return { score: Math.max(0, score), factors }
  }

  /**
   * Calculate Income Stability Component (25% weight)
   */
  private static calculateIncomeStability(
    data: CreditDataSources,
  ): Omit<ScoreComponent, 'weight' | 'weightedScore'> {
    const factors: ScoreFactor[] = []
    let score = 100

    const salary = data.alternativeData.salaryData

    // Employment type
    if (salary.employmentType === 'Permanent') {
      factors.push({
        name: 'Permanent Employment',
        impact: 'positive',
        value: salary.employer,
        description: 'Stable employment status',
      })
    } else if (salary.employmentType === 'Contract') {
      score -= 10
      factors.push({
        name: 'Contract Employment',
        impact: 'neutral',
        value: salary.employer,
        description: 'Contract-based income',
      })
    } else {
      score -= 20
      factors.push({
        name: 'Freelance Income',
        impact: 'negative',
        value: 'Self-employed',
        description: 'Variable income source',
      })
    }

    // Employment duration
    if (salary.employmentDuration >= 24) {
      factors.push({
        name: 'Long Employment History',
        impact: 'positive',
        value: `${salary.employmentDuration} months`,
        description: 'Stable job tenure',
      })
    } else if (salary.employmentDuration >= 12) {
      score -= 5
      factors.push({
        name: 'Moderate Employment History',
        impact: 'neutral',
        value: `${salary.employmentDuration} months`,
        description: 'Reasonable job tenure',
      })
    } else {
      score -= 15
      factors.push({
        name: 'Short Employment History',
        impact: 'negative',
        value: `${salary.employmentDuration} months`,
        description: 'Recent job change',
      })
    }

    // Income consistency
    if (salary.incomeConsistency >= 90) {
      factors.push({
        name: 'Highly Consistent Income',
        impact: 'positive',
        value: `${salary.incomeConsistency}%`,
        description: 'Regular salary payments',
      })
    } else if (salary.incomeConsistency >= 70) {
      score -= 5
      factors.push({
        name: 'Moderately Consistent Income',
        impact: 'neutral',
        value: `${salary.incomeConsistency}%`,
        description: 'Some income variation',
      })
    } else {
      score -= 15
      factors.push({
        name: 'Inconsistent Income',
        impact: 'negative',
        value: `${salary.incomeConsistency}%`,
        description: 'Irregular income pattern',
      })
    }

    return { score: Math.max(0, score), factors }
  }

  /**
   * Calculate Debt Exposure Component (15% weight)
   */
  private static calculateDebtExposure(
    data: CreditDataSources,
  ): Omit<ScoreComponent, 'weight' | 'weightedScore'> {
    const factors: ScoreFactor[] = []
    let score = 100

    // Calculate rent-to-income ratio
    const monthlyRent =
      data.platformData.rentPayments.length > 0
        ? data.platformData.rentPayments[0].amount
        : 0
    const rentToIncome =
      (monthlyRent / data.alternativeData.salaryData.monthlyIncome) * 100

    if (rentToIncome <= 30) {
      factors.push({
        name: 'Healthy Rent-to-Income Ratio',
        impact: 'positive',
        value: `${rentToIncome.toFixed(1)}%`,
        description: 'Low debt burden',
      })
    } else if (rentToIncome <= 40) {
      score -= 10
      factors.push({
        name: 'Moderate Rent-to-Income Ratio',
        impact: 'neutral',
        value: `${rentToIncome.toFixed(1)}%`,
        description: 'Acceptable debt level',
      })
    } else {
      score -= 25
      factors.push({
        name: 'High Rent-to-Income Ratio',
        impact: 'negative',
        value: `${rentToIncome.toFixed(1)}%`,
        description: 'High debt burden',
      })
    }

    // Bank statement analysis
    if (data.alternativeData.bankStatements.length > 0) {
      const avgBalance =
        data.alternativeData.bankStatements.reduce(
          (sum, b) => sum + b.averageBalance,
          0,
        ) / data.alternativeData.bankStatements.length

      if (avgBalance >= monthlyRent * 3) {
        factors.push({
          name: 'Strong Savings Buffer',
          impact: 'positive',
          value: `${(avgBalance / monthlyRent).toFixed(1)}x rent`,
          description: 'Healthy emergency fund',
        })
      } else if (avgBalance >= monthlyRent) {
        score -= 5
        factors.push({
          name: 'Moderate Savings',
          impact: 'neutral',
          value: `${(avgBalance / monthlyRent).toFixed(1)}x rent`,
          description: 'Some savings available',
        })
      } else {
        score -= 15
        factors.push({
          name: 'Low Savings',
          impact: 'negative',
          value: `${(avgBalance / monthlyRent).toFixed(1)}x rent`,
          description: 'Limited emergency fund',
        })
      }
    }

    return { score: Math.max(0, score), factors }
  }

  /**
   * Calculate Platform Behavior Component (10% weight)
   */
  private static calculatePlatformBehavior(
    data: CreditDataSources,
  ): Omit<ScoreComponent, 'weight' | 'weightedScore'> {
    const factors: ScoreFactor[] = []
    let score = 100

    // Autopay usage
    if (data.platformData.autopayEnabled) {
      factors.push({
        name: 'Autopay Enabled',
        impact: 'positive',
        value: 'Yes',
        description: 'Automated payment setup',
      })
    } else {
      score -= 10
      factors.push({
        name: 'Manual Payments',
        impact: 'negative',
        value: 'No autopay',
        description: 'Higher risk of missed payments',
      })
    }

    // Disputes
    if (data.platformData.disputes === 0) {
      factors.push({
        name: 'No Disputes',
        impact: 'positive',
        value: '0',
        description: 'Clean dispute record',
      })
    } else if (data.platformData.disputes <= 2) {
      score -= 5
      factors.push({
        name: 'Few Disputes',
        impact: 'neutral',
        value: data.platformData.disputes.toString(),
        description: 'Some disputes raised',
      })
    } else {
      score -= 15
      factors.push({
        name: 'Multiple Disputes',
        impact: 'negative',
        value: data.platformData.disputes.toString(),
        description: 'Frequent disputes',
      })
    }

    return { score: Math.max(0, score), factors }
  }

  /**
   * Calculate Stability & Tenure Component (10% weight)
   */
  private static calculateStabilityTenure(
    data: CreditDataSources,
  ): Omit<ScoreComponent, 'weight' | 'weightedScore'> {
    const factors: ScoreFactor[] = []
    let score = 100

    // Tenancy duration
    if (data.platformData.tenancyDuration >= 24) {
      factors.push({
        name: 'Long Tenancy',
        impact: 'positive',
        value: `${data.platformData.tenancyDuration} months`,
        description: 'Stable housing history',
      })
    } else if (data.platformData.tenancyDuration >= 12) {
      score -= 5
      factors.push({
        name: 'Moderate Tenancy',
        impact: 'neutral',
        value: `${data.platformData.tenancyDuration} months`,
        description: 'Reasonable tenancy length',
      })
    } else {
      score -= 10
      factors.push({
        name: 'Short Tenancy',
        impact: 'negative',
        value: `${data.platformData.tenancyDuration} months`,
        description: 'New tenant',
      })
    }

    return { score: Math.max(0, score), factors }
  }

  /**
   * Calculate Rent-Specific Score (R-Score)
   */
  private static calculateRentScore(
    data: CreditDataSources,
    generalScore: number,
  ): number {
    // R-Score emphasizes rent-specific factors
    const rentPaymentWeight = 0.5 // 50% weight on rent payments
    const incomeStabilityWeight = 0.3 // 30% weight on income
    const tenancyWeight = 0.2 // 20% weight on tenancy history

    const rentPaymentScore =
      data.platformData.rentPayments.length > 0
        ? (data.platformData.rentPayments.filter((p) => p.status === 'on-time')
            .length /
            data.platformData.rentPayments.length) *
          100
        : 50

    const incomeScore = data.alternativeData.salaryData.incomeConsistency

    const tenancyScore = Math.min(
      100,
      (data.platformData.tenancyDuration / 24) * 100,
    )

    const rScore =
      rentPaymentScore * rentPaymentWeight +
      incomeScore * incomeStabilityWeight +
      tenancyScore * tenancyWeight

    return Math.round(rScore * 8.5) // Convert to 0-850 scale
  }

  /**
   * Determine Risk Tier
   */
  private static determineRiskTier(score: number): RiskTier {
    for (const [tier, range] of Object.entries(this.RISK_TIERS)) {
      if (score >= range.min && score <= range.max) {
        return tier as RiskTier
      }
    }
    return 'D'
  }

  /**
   * Calculate Default Probability
   */
  private static calculateDefaultProbability(
    score: number,
    data: CreditDataSources,
  ): number {
    // Base probability from score
    let probability = 100 - (score / 850) * 100

    // Adjust based on payment history
    if (data.platformData.missedPayments > 0) {
      probability += data.platformData.missedPayments * 5
    }

    // Adjust based on rent-to-income
    const monthlyRent = data.platformData.rentPayments[0]?.amount || 0
    const rentToIncome =
      (monthlyRent / data.alternativeData.salaryData.monthlyIncome) * 100
    if (rentToIncome > 40) {
      probability += (rentToIncome - 40) * 2
    }

    return Math.min(100, Math.max(0, probability))
  }

  /**
   * Calculate Data Completeness
   */
  private static calculateDataCompleteness(data: CreditDataSources): number {
    let completeness = 0
    let totalFields = 0

    // Check bureau data
    totalFields += 1
    if (data.bureauData && data.bureauData.length > 0) completeness += 1

    // Check bank statements
    totalFields += 1
    if (data.alternativeData.bankStatements.length >= 3) completeness += 1

    // Check salary data
    totalFields += 1
    if (data.alternativeData.salaryData.monthlyIncome > 0) completeness += 1

    // Check utility payments
    totalFields += 1
    if (data.alternativeData.utilityPayments.length > 0) completeness += 1

    // Check platform data
    totalFields += 1
    if (data.platformData.rentPayments.length > 0) completeness += 1

    return Math.round((completeness / totalFields) * 100)
  }

  /**
   * Generate Credit Decision
   */
  static generateDecision(
    score: CreditScore,
    requestedRent: number,
    monthlyIncome: number,
  ): CreditDecision {
    const rules = this.getDecisionRules(score.riskTier)
    const rentToIncome = (requestedRent / monthlyIncome) * 100

    const approved =
      score.generalScore >= rules.minScore &&
      rentToIncome <= rules.maxRentToIncome
    const maxRentAmount = monthlyIncome * (rules.maxRentToIncome / 100)
    const interestRate = this.calculateInterestRate(
      score.generalScore,
      rules.interestRateRange,
    )

    const conditions: string[] = []
    const reasoning: string[] = []

    if (approved) {
      reasoning.push(
        `Credit score ${score.generalScore} meets minimum requirement of ${rules.minScore}`,
      )
      reasoning.push(
        `Rent-to-income ratio ${rentToIncome.toFixed(1)}% is within acceptable range`,
      )
    } else {
      reasoning.push(
        `Credit score ${score.generalScore} below minimum requirement of ${rules.minScore}`,
      )
      if (rentToIncome > rules.maxRentToIncome) {
        reasoning.push(
          `Rent-to-income ratio ${rentToIncome.toFixed(1)}% exceeds maximum of ${rules.maxRentToIncome}%`,
        )
      }
    }

    if (rules.requiresGuarantor) {
      conditions.push('Guarantor required')
    }

    if (rules.requiresInsurance) {
      conditions.push('Rent insurance required')
    }

    if (rules.autopayRequired) {
      conditions.push('Autopay enrollment mandatory')
    }

    return {
      approved,
      riskTier: score.riskTier,
      maxRentAmount,
      interestRate,
      requiresGuarantor: rules.requiresGuarantor,
      requiresInsurance: rules.requiresInsurance,
      depositAdjustment: rules.depositMultiplier,
      autopayRequired: rules.autopayRequired,
      conditions,
      reasoning,
    }
  }

  /**
   * Get Decision Rules for Risk Tier
   */
  private static getDecisionRules(tier: RiskTier): DecisionRules {
    const rules: Record<RiskTier, DecisionRules> = {
      A: {
        riskTier: 'A',
        minScore: 750,
        maxScore: 850,
        maxRentToIncome: 40,
        interestRateRange: [18, 22],
        requiresGuarantor: false,
        requiresInsurance: false,
        depositMultiplier: 1.0,
        autopayRequired: false,
      },
      B: {
        riskTier: 'B',
        minScore: 650,
        maxScore: 749,
        maxRentToIncome: 35,
        interestRateRange: [22, 26],
        requiresGuarantor: false,
        requiresInsurance: false,
        depositMultiplier: 1.5,
        autopayRequired: true,
      },
      C: {
        riskTier: 'C',
        minScore: 550,
        maxScore: 649,
        maxRentToIncome: 30,
        interestRateRange: [26, 30],
        requiresGuarantor: true,
        requiresInsurance: true,
        depositMultiplier: 2.0,
        autopayRequired: true,
      },
      D: {
        riskTier: 'D',
        minScore: 0,
        maxScore: 549,
        maxRentToIncome: 25,
        interestRateRange: [30, 35],
        requiresGuarantor: true,
        requiresInsurance: true,
        depositMultiplier: 3.0,
        autopayRequired: true,
      },
    }

    return rules[tier]
  }

  /**
   * Calculate Interest Rate based on score
   */
  private static calculateInterestRate(
    score: number,
    range: [number, number],
  ): number {
    const [min, max] = range
    const normalized = (score - 550) / (850 - 550) // Normalize to 0-1
    const rate = max - normalized * (max - min)
    return Math.round(rate * 100) / 100
  }

  /**
   * Generate Improvement Recommendations
   */
  static generateRecommendations(score: CreditScore): Recommendation[] {
    const recommendations: Recommendation[] = []

    // Payment history recommendations
    if (score.components.paymentHistory.score < 90) {
      recommendations.push({
        id: 'enable-autopay',
        title: 'Enable Autopay',
        description: 'Set up automatic payments to never miss a due date',
        impact: 'high',
        scoreIncrease: 30,
        timeToImpact: '3 months',
        actionRequired: 'Enable autopay in payment settings',
        completed: false,
      })
    }

    // Income stability recommendations
    if (score.components.incomeStability.score < 80) {
      recommendations.push({
        id: 'verify-employment',
        title: 'Verify Employment',
        description: 'Connect your employer for automated income verification',
        impact: 'high',
        scoreIncrease: 25,
        timeToImpact: 'Immediate',
        actionRequired: 'Complete employment verification',
        completed: false,
      })
    }

    // Debt exposure recommendations
    if (score.components.debtExposure.score < 85) {
      recommendations.push({
        id: 'link-bank-account',
        title: 'Link Bank Account',
        description: 'Connect your bank account to show savings and cash flow',
        impact: 'medium',
        scoreIncrease: 20,
        timeToImpact: 'Immediate',
        actionRequired: 'Link bank account in settings',
        completed: false,
      })
    }

    // Platform behavior recommendations
    if (score.components.platformBehavior.score < 90) {
      recommendations.push({
        id: 'maintain-clean-record',
        title: 'Maintain Clean Record',
        description: 'Avoid disputes and support tickets for better score',
        impact: 'low',
        scoreIncrease: 10,
        timeToImpact: '6 months',
        actionRequired: 'Communicate proactively with landlord',
        completed: false,
      })
    }

    return recommendations.sort((a, b) => {
      const impactOrder = { high: 0, medium: 1, low: 2 }
      return impactOrder[a.impact] - impactOrder[b.impact]
    })
  }
}

// Export helper functions
export function formatCreditScore(score: number): string {
  return score.toString()
}

export function getRiskTierColor(tier: RiskTier): string {
  const colors = {
    A: 'text-green-600 bg-green-50 border-green-200',
    B: 'text-blue-600 bg-blue-50 border-blue-200',
    C: 'text-yellow-600 bg-yellow-50 border-yellow-200',
    D: 'text-red-600 bg-red-50 border-red-200',
  }
  return colors[tier]
}

export function getRiskTierLabel(tier: RiskTier): string {
  const labels = {
    A: 'Very Low Risk',
    B: 'Low Risk',
    C: 'Medium Risk',
    D: 'High Risk',
  }
  return labels[tier]
}
